import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { documentsApi, type DocumentSummary } from '../api/documents'
import { validationRulesApi } from '../api/validationRules'
import { DocumentAiReadDialog } from '../components/DocumentAiReadDialog'
import { FileDropZone } from '../components/FileDropZone'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button, Callout, Card, Checkbox, DataTable, EmptyState, FilterBar, Icon, IconButton, IconButtonLink, LoadingState, Page, PageHeader,
  Select, StatusPill, TextField,
} from '../components/ui'

// Label per il file input: non esiste un componente ui per <label> a forma di bottone, quindi si riusano le classi dei bottoni ui.
const LABEL_BUTTON = 'md-button md-button--filled md-button--sm md-tone--primary md-state md-touch'

/**
 * TASK-0032 / ADR-010: real document archive — replaces the `/documents` `PlaceholderPage`. Today
 * (per ADR-010's own context) nothing else in the frontend links here with an entity context, so
 * this page is both a standalone "Drive" browser (entityType/entityId picked via the form below)
 * AND a deep-link target for a future entity detail page: `entityType`/`entityId` query params
 * (same two params `GET /api/documents` has always accepted) pre-fill the filter when present.
 *
 * Same pattern as ApprovalsPage.tsx/FunnelsPage.tsx: no client-side route gating or role check.
 * The real gate is the backend — `[Authorize(PolicyNames.Authenticated)]` on every action here,
 * plus `[Authorize(PolicyNames.Admin)]` specifically on delete (ADR-010 §5's storage-then-row
 * ordering also lives server-side). A non-Admin caller sees the delete button same as anyone else
 * and simply gets the 403's message if they use it — this page does not try to hide it.
 */

const ENTITY_TYPES = ['Customer', 'Contract', 'Lead', 'Company', 'Operator'] as const

// TASK-0254 (REQ-022-003): spec §24's own worked example uses a 30-day "in scadenza" window, so
// this reuses that number rather than inventing a new one.
const EXPIRING_SOON_WINDOW_DAYS = 30

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * TASK-0254 (REQ-022-003): classifies `expirationDate` (the ISO `date` string
 * `DocumentSummary.expirationDate` carries, e.g. `"2027-01-31"`) against today's LOCAL date.
 * Parsed as `y-m-d` components rather than `new Date(expirationDate)` — the latter treats a
 * bare `"YYYY-MM-DD"` string as UTC midnight, which would flip a same-day expiration to
 * "expired" a day early for any caller west of UTC. `today` is likewise a local-midnight
 * `Date`, so both sides of the comparison use the same clock.
 */
function classifyExpiration(expirationDate: string | null | undefined): 'expired' | 'expiring-soon' | 'ok' | 'none' {
  if (!expirationDate) return 'none'
  const parts = expirationDate.split('-').map(Number)
  if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return 'none'
  const [year, month, day] = parts
  const expiry = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'expired'
  if (diffDays <= EXPIRING_SOON_WINDOW_DAYS) return 'expiring-soon'
  return 'ok'
}

/** TASK-0254 (REQ-022-003): the "Scaduto"/"In scadenza" badge cell for the Scadenza column. */
function ExpirationCell({ expirationDate }: { expirationDate?: string | null | undefined }) {
  const status = classifyExpiration(expirationDate)
  if (status === 'none') return <>—</>
  return (
    <>
      {expirationDate}
      {status === 'expired' && <StatusPill tone="error" className="ms-1">Scaduto</StatusPill>}
      {status === 'expiring-soon' && <StatusPill tone="warning" className="ms-1">In scadenza</StatusPill>}
    </>
  )
}

export function DocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [entityType, setEntityType] = useState(searchParams.get('entityType') || ENTITY_TYPES[0])
  const [entityIdInput, setEntityIdInput] = useState(searchParams.get('entityId') || '1')

  const [items, setItems] = useState<DocumentSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [uploadNotes, setUploadNotes] = useState('')
  const [uploadExpirationDate, setUploadExpirationDate] = useState('')
  const [uploadDocumentType, setUploadDocumentType] = useState('')
  const [uploadStatus, setUploadStatus] = useState('')
  // TASK-0254 (REQ-022-003): purely client-side filter over the already-loaded `items` list — no
  // new query parameter, no backend edit, per the task's own plan.
  const [onlyExpired, setOnlyExpired] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // F-141 (REQ-041-001): zona di trascinamento, dietro il flag; carica con lo stesso endpoint del pulsante.
  const dropZoneOn = useFeatureFlag('contratti.salvataggio_automatico')
  const aiReadOn = useFeatureFlag('documenti.lettura_automatica')
  const [aiReadDoc, setAiReadDoc] = useState<DocumentSummary | null>(null)
  // F-056: tipi di documento richiesti dalle regole di completezza attive, come suggerimenti per il
  // campo "Tipo documento" (il campo resta testo libero; se la lettura fallisce non cambia nulla).
  const [requiredTypes, setRequiredTypes] = useState<string[]>([])
  useEffect(() => {
    validationRulesApi.documentTypes().then(setRequiredTypes).catch(() => setRequiredTypes([]))
  }, [])

  const entityId = Number(entityIdInput) || 0

  const load = useCallback(async (type: string, id: number) => {
    if (!type || !id) {
      setItems([])
      return
    }
    setLoading(true)
    setLoadError('')
    try {
      const result = await documentsApi.list(type, id)
      setItems(result)
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const type = searchParams.get('entityType') || ENTITY_TYPES[0]
    const idInput = searchParams.get('entityId') || '1'
    setEntityType(type)
    setEntityIdInput(idInput)
    void load(type, Number(idInput) || 0)
  }, [searchParams, load])

  function applyFilter() {
    setSearchParams({ entityType, entityId: entityIdInput })
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    await uploadFiles([file])
  }

  /** Stesso caricamento del pulsante (`POST /api/documents`, stessi campi opzionali), per uno o piu' file. */
  async function uploadFiles(files: File[]) {
    if (files.length === 0 || !entityType || !entityId) return
    setActionError('')
    setUploading(true)
    // TASK-0160: read from state at the moment the upload fires — the file input's onChange is
    // the only trigger, there is no separate confirmation step where this could be read later.
    const notes = uploadNotes.trim()
    // TASK-0205: same read-at-upload-time pattern as notes above.
    const expirationDate = uploadExpirationDate.trim()
    // TASK-0249: same read-at-upload-time pattern as notes/expirationDate above.
    const documentType = uploadDocumentType.trim()
    const status = uploadStatus.trim()
    try {
      for (const file of files) {
        await documentsApi.upload(
          entityType, entityId, file, notes || undefined, expirationDate || undefined,
          documentType || undefined, status || undefined,
        )
      }
      await load(entityType, entityId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Errore durante il caricamento')
    } finally {
      setUploading(false)
      setUploadNotes('')
      setUploadExpirationDate('')
      setUploadDocumentType('')
      setUploadStatus('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDelete(id: number) {
    setActionError('')
    setDeletingId(id)
    try {
      await documentsApi.remove(id)
      await load(entityType, entityId)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Errore durante la cancellazione')
    } finally {
      setDeletingId(null)
    }
  }

  // TASK-0254 (REQ-022-003): client-side only — filters the already-loaded `items`, never
  // re-fetches, so toggling it costs no request.
  const visibleItems = onlyExpired
    ? items.filter(d => classifyExpiration(d.expirationDate) === 'expired')
    : items

  return (
    <Page>
      <PageHeader title="Drive" icon="folder_open" />

      <FilterBar
        actions={
          <>
            <Button variant="tonal" onClick={applyFilter}>Filtra</Button>
            <label className={LABEL_BUTTON}>
              <Icon name="upload_file" size={18} />
              {uploading ? 'Caricamento...' : 'Carica documento'}
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                disabled={uploading || !entityType || !entityId}
                onChange={e => void handleUpload(e)}
              />
            </label>
          </>
        }
      >
        <Select label="Tipo entità" value={entityType} onChange={e => setEntityType(e.target.value)}>
          {ENTITY_TYPES.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Select>
        <TextField
          type="number"
          min={1}
          label="ID entità"
          value={entityIdInput}
          onChange={e => setEntityIdInput(e.target.value)}
        />
        {/* TASK-0254 (REQ-022-003): "Solo scaduti" — client-side filter, see visibleItems above. */}
        <Checkbox
          id="onlyExpiredCheck"
          label="Solo scaduti"
          checked={onlyExpired}
          onChange={e => setOnlyExpired(e.target.checked)}
        />
        <TextField
          type="text"
          label="Tipo documento (opzionale)"
          placeholder="Contratto, Fattura..."
          list="required-document-types"
          value={uploadDocumentType}
          onChange={e => setUploadDocumentType(e.target.value)}
          disabled={uploading}
        />
        <datalist id="required-document-types">
          {requiredTypes.map(t => <option key={t} value={t} />)}
        </datalist>
        <TextField
          type="text"
          label="Stato (opzionale)"
          placeholder="Stato del documento..."
          value={uploadStatus}
          onChange={e => setUploadStatus(e.target.value)}
          disabled={uploading}
        />
        <TextField
          type="text"
          label="Note (opzionale)"
          placeholder="Note sul documento..."
          value={uploadNotes}
          onChange={e => setUploadNotes(e.target.value)}
          disabled={uploading}
        />
        <TextField
          type="date"
          label="Data di scadenza (opzionale)"
          value={uploadExpirationDate}
          onChange={e => setUploadExpirationDate(e.target.value)}
          disabled={uploading}
        />
      </FilterBar>

      {actionError && <Callout tone="error">{actionError}</Callout>}

      {dropZoneOn && (
        <FileDropZone
          onFiles={files => uploadFiles(files)}
          busy={uploading}
          disabled={!entityType || !entityId}
          label="Trascina qui i file da caricare per questa entità (o clicca per sceglierli)"
        />
      )}

      {loading ? (
        <Card variant="outlined"><LoadingState /></Card>
      ) : loadError ? (
        <Callout tone="error">Non è stato possibile caricare i documenti. {loadError}</Callout>
      ) : items.length === 0 ? (
        <EmptyState icon="folder_open" title="Nessun documento per questa entità" />
      ) : visibleItems.length === 0 ? (
        <EmptyState icon="folder_open" title="Nessun documento scaduto per questa entità" />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Tipo</th>
              <th>Dimensione</th>
              <th>Caricato il</th>
              <th>Tipo documento</th>
              <th>Stato</th>
              <th>Note</th>
              <th>Scadenza</th>
              <th className="text-end">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map(d => (
              <tr key={d.id}>
                <td className="md-typescale-emphasized-body-medium">
                  {d.name}
                  {d.uploadStatus === 'InCoda' && <StatusPill tone="info" className="ms-2">In coda</StatusPill>}
                  {d.uploadStatus === 'Errore' && <StatusPill tone="error" className="ms-2">Errore di caricamento</StatusPill>}
                </td>
                <td className="text-muted md-typescale-body-small">{d.mimeType}</td>
                <td className="text-muted md-typescale-body-small md-num">{formatBytes(d.sizeBytes)}</td>
                <td className="text-muted md-typescale-body-small">{new Date(d.createdAt).toLocaleString('it-IT')}</td>
                <td className="text-muted md-typescale-body-small">{d.documentType || '—'}</td>
                <td className="text-muted md-typescale-body-small">{d.status || '—'}</td>
                <td className="text-muted md-typescale-body-small">{d.notes || '—'}</td>
                <td className="text-muted md-typescale-body-small">
                  <ExpirationCell expirationDate={d.expirationDate} />
                </td>
                <td className="text-end">
                  <IconButtonLink
                    href={documentsApi.previewUrl(d.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    icon="visibility"
                    aria-label="Anteprima"
                  />
                  <IconButtonLink
                    href={documentsApi.contentUrl(d.id)}
                    download={d.name}
                    size="sm"
                    icon="download"
                    aria-label="Scarica"
                  />
                  {aiReadOn && (d.uploadStatus ?? 'Completato') === 'Completato' && (
                    <IconButton
                      icon="document_scanner"
                      size="sm"
                      aria-label="Leggi con IA"
                      onClick={() => setAiReadDoc(d)}
                    />
                  )}
                  <IconButton
                    icon="delete"
                    size="sm"
                    aria-label="Elimina"
                    disabled={deletingId === d.id}
                    onClick={() => void handleDelete(d.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <DocumentAiReadDialog
        documentId={aiReadDoc?.id ?? null}
        documentName={aiReadDoc?.name ?? ''}
        onClose={() => setAiReadDoc(null)}
      />
    </Page>
  )
}
